import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock3, Send, CheckCircle2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { BRAND } from "../lib/brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const FORMSPEE_ENDPOINT = "https://formspree.io/f/mlgznjpq";

const initialForm = { name: "", email: "", subject: "", message: "" };

const ContactPage = () => {
  const { t, lang } = useI18n();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      const formData = {
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
        language: lang,
        _subject: `New contact form submission from ${form.name}`,
        _replyto: form.email
      };

      await axios.post(FORMSPEE_ENDPOINT, formData, {
        headers: {
          'Accept': 'application/json'
        }
      });

      toast.success(t("contact.form.success"));
      setSent(true);
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error(t("contact.form.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const infoItems = [
    { icon: Mail, label: t("contact.info.email"), value: BRAND.email, href: `mailto:${BRAND.email}`, testid: "contact-info-email" },
    { icon: Phone, label: t("contact.info.phone"), value: BRAND.phone, href: `tel:${BRAND.phone.replace(/\s/g, "")}`, testid: "contact-info-phone" },
    { icon: MapPin, label: t("contact.info.address"), value: BRAND.address, testid: "contact-info-address" },
    { icon: Clock3, label: t("contact.info.hours"), value: t("contact.info.hoursValue"), testid: "contact-info-hours" },
  ];

  return (
    <div data-testid="page-contact">
      <section className="relative overflow-hidden pt-8 pb-10 md:pt-14 md:pb-16">
        <div className="absolute inset-0 grid-mesh opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl">
            <div className="font-mono-accent text-[11px] text-muted-foreground">
              {t("contact.overline")}
            </div>
            <h1 className="mt-4 font-display text-5xl md:text-6xl font-black tracking-tighter leading-[0.95]">
              {t("contact.title")}
            </h1>
            <p className="mt-5 text-muted-foreground md:text-lg">
              {t("contact.sub")}
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 grid gap-10 lg:grid-cols-12">
          {/* Info */}
          <aside className="lg:col-span-5 space-y-4">
            {infoItems.map((it) => (
              <a
                key={it.testid}
                href={it.href}
                data-testid={it.testid}
                className="group relative block overflow-hidden rounded-sm glass-card p-5 md:p-6 transition hover:-translate-y-0.5"
              >
                <div className="absolute inset-0 dot-mesh opacity-50 pointer-events-none" />
                <div className="relative flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-brand-navy text-brand-cyan ring-1 ring-brand-cyan/30">
                    <it.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-mono-accent text-[10px] text-muted-foreground">{it.label}</div>
                    <div className="mt-1 font-semibold">{it.value}</div>
                  </div>
                </div>
              </a>
            ))}
           
          </aside>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="relative overflow-hidden rounded-sm glass-card p-6 md:p-10"
            >
              <div className="absolute inset-0 dot-mesh opacity-40 pointer-events-none" />
              <div className="relative grid gap-5 md:grid-cols-2">
                <div className="md:col-span-1">
                  <Label htmlFor="name" className="font-mono-accent text-[10px] text-muted-foreground">
                    {t("contact.form.name")}
                  </Label>
                  <Input
                    id="mlgznjpq"
                    data-testid="contact-input-name"
                    required
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder={t("contact.form.namePh")}
                    className="mt-2 rounded-sm border-border bg-background/60 focus-visible:ring-brand-cyan"
                  />
                </div>
                <div className="md:col-span-1">
                  <Label htmlFor="email" className="font-mono-accent text-[10px] text-muted-foreground">
                    {t("contact.form.email")}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-input-email"
                    required
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder={t("contact.form.emailPh")}
                    className="mt-2 rounded-sm border-border bg-background/60 focus-visible:ring-brand-cyan"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="subject" className="font-mono-accent text-[10px] text-muted-foreground">
                    {t("contact.form.subject")}
                  </Label>
                  <Input
                    id="subject"
                    data-testid="contact-input-subject"
                    required
                    value={form.subject}
                    onChange={onChange("subject")}
                    placeholder={t("contact.form.subjectPh")}
                    className="mt-2 rounded-sm border-border bg-background/60 focus-visible:ring-brand-cyan"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="message" className="font-mono-accent text-[10px] text-muted-foreground">
                    {t("contact.form.message")}
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-input-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder={t("contact.form.messagePh")}
                    className="mt-2 rounded-sm border-border bg-background/60 focus-visible:ring-brand-cyan"
                  />
                </div>
              </div>
              <div className="relative mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="font-mono-accent text-[10px] text-muted-foreground">
                  {lang === "bg" ? "Никога не споделяме информацията ви." : "We never share your information."}
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="rounded-sm font-mono-accent text-xs bg-brand-navy text-white hover:bg-brand-cyan hover:text-brand-navy dark:bg-brand-cyan dark:text-brand-navy dark:hover:bg-white btn-press"
                >
                  {submitting ? t("common.loading") : t("contact.form.submit")}
                  <Send className="ml-2 h-3.5 w-3.5" />
                </Button>
              </div>
              {sent && (
                <div
                  data-testid="contact-success-banner"
                  className="relative mt-6 flex items-start gap-3 rounded-sm border border-brand-cyan/40 bg-brand-cyan/10 p-4 text-sm"
                >
                  <CheckCircle2 className="h-4 w-4 text-brand-cyan mt-0.5" />
                  <span>{t("contact.form.success")}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
